/**
 * IOSAppIcon — tiny, recognizable iOS app logos for the fake push
 * banners in the Apocalypse trick. Renders a squircle background tile
 * with a white (or color) SVG glyph at typical iOS proportions. All
 * icons are inline SVG, no external assets, so they survive the disguise
 * constraint (everything ships in-app).
 *
 * Lookup is case- and whitespace-insensitive. Unknown apps fall back to
 * the generic single-letter tile (preserves the previous behaviour for
 * magician-defined custom apps).
 *
 * Sizes: the tile fills its container — pass via `className` (w-7 h-7 is
 * the banner default; w-10 h-10 also looks correct for thumbnails).
 */

const SQUIRCLE = "rounded-[7px]"; // close to iOS app-icon corner radius at small sizes

function Tile({ children, bg, className = "", style }) {
  return (
    <div
      className={`${SQUIRCLE} ${className} shrink-0 flex items-center justify-center overflow-hidden ${bg}`}
      style={style}
    >
      {children}
    </div>
  );
}

/** Generic letter fallback — same look as the original placeholder. */
function FallbackTile({ name, className }) {
  return (
    <Tile bg="bg-gradient-to-br from-blue-500 to-blue-700" className={className}>
      <span className="text-white text-[12px] font-semibold leading-none">
        {(name || "?").slice(0, 1).toUpperCase()}
      </span>
    </Tile>
  );
}

/** Mail — blue gradient, white envelope. */
function MailIcon({ className }) {
  return (
    <Tile
      bg=""
      className={className}
      style={{ background: "linear-gradient(180deg, #2EB4FF 0%, #007AFF 100%)" }}
    >
      <svg viewBox="0 0 24 24" className="w-[68%] h-[68%]" fill="none">
        <rect x="3" y="6" width="18" height="12" rx="2" fill="#fff" />
        <path d="M4 7.5l8 5.5 8-5.5" stroke="#2EB4FF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </Tile>
  );
}

/** Messages — green gradient, white speech bubble. */
function MessagesIcon({ className }) {
  return (
    <Tile
      bg=""
      className={className}
      style={{ background: "linear-gradient(180deg, #5BF675 0%, #1FCC4A 100%)" }}
    >
      <svg viewBox="0 0 24 24" className="w-[72%] h-[72%]" fill="#fff">
        <path d="M12 4.2c-4.85 0-8.7 3-8.7 6.95 0 2.2 1.2 4.15 3.1 5.45-.1 1-.55 2.05-1.35 2.7-.2.15-.1.45.15.45 1.95-.05 3.55-.7 4.55-1.5.7.15 1.45.25 2.25.25 4.85 0 8.7-3 8.7-6.95S16.85 4.2 12 4.2z" />
      </svg>
    </Tile>
  );
}

/** WhatsApp — green, white phone-in-bubble. */
function WhatsAppIcon({ className }) {
  return (
    <Tile bg="" className={className} style={{ backgroundColor: "#25D366" }}>
      <svg viewBox="0 0 32 32" className="w-[78%] h-[78%]" fill="#fff">
        <path d="M16 3C8.82 3 3 8.82 3 16c0 2.3.6 4.46 1.65 6.33L3 29l6.83-1.6A12.94 12.94 0 0 0 16 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.6c-1.96 0-3.86-.52-5.52-1.5l-.4-.23-4.05.95.96-3.95-.26-.41A10.55 10.55 0 0 1 5.4 16C5.4 10.16 10.16 5.4 16 5.4S26.6 10.16 26.6 16 21.84 26.6 16 26.6z" />
        <path d="M22 19.05c-.32-.16-1.9-.94-2.2-1.04-.3-.11-.51-.16-.72.16-.21.32-.83 1.04-1.02 1.25-.19.21-.37.24-.69.08-.32-.16-1.36-.5-2.59-1.6a9.7 9.7 0 0 1-1.8-2.23c-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55h-.61c-.21 0-.56.08-.85.4-.29.32-1.1 1.07-1.1 2.6 0 1.54 1.13 3.02 1.29 3.23.16.21 2.22 3.4 5.4 4.77.75.32 1.34.52 1.8.66.76.24 1.45.21 2-.13.61-.36 1.9-.78 2.17-1.53.27-.75.27-1.39.19-1.53-.08-.13-.29-.21-.61-.37z" />
      </svg>
    </Tile>
  );
}

/** Phone — green gradient, white phone receiver. */
function PhoneIcon({ className }) {
  return (
    <Tile
      bg=""
      className={className}
      style={{ background: "linear-gradient(180deg, #6BE56C 0%, #28C840 100%)" }}
    >
      <svg viewBox="0 0 24 24" className="w-[64%] h-[64%]" fill="#fff">
        <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A18 18 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" />
      </svg>
    </Tile>
  );
}

/** FaceTime — same green, video-camera glyph. */
function FaceTimeIcon({ className }) {
  return (
    <Tile
      bg=""
      className={className}
      style={{ background: "linear-gradient(180deg, #6BE56C 0%, #28C840 100%)" }}
    >
      <svg viewBox="0 0 24 24" className="w-[68%] h-[68%]" fill="#fff">
        <path d="M4 7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7zm15 1.5l3-1.7v10.4l-3-1.7v-7z" />
      </svg>
    </Tile>
  );
}

/** Calendar — white tile, red weekday header + black day number. */
function CalendarIcon({ className }) {
  const today = new Date();
  const weekday = today.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase().slice(0, 3);
  const day = today.getDate();
  return (
    <Tile bg="bg-white" className={className}>
      <div className="flex flex-col items-center justify-center w-full h-full leading-none">
        <span className="text-[6px] font-bold text-[#FF3B30] tracking-wide">{weekday}</span>
        <span className="text-[14px] font-semibold text-black leading-none mt-[1px]">{day}</span>
      </div>
    </Tile>
  );
}

/** Settings — real Apple icon PNG. */
function SettingsIcon({ className }) {
  return <PngIcon src="/icons/settings.webp" className={className} />;
}

/** Photos — real Apple icon PNG. */
function PhotosIcon({ className }) {
  return <PngIcon src="/icons/photos.png" className={className} />;
}

/**
 * Real iOS icon PNG. The source assets ship their own squircle mask and
 * gradients, so we render the image full-bleed at the requested size —
 * no Tile background, no extra rounding. Alt is empty because the icon
 * is decorative next to the banner's app name.
 */
function PngIcon({ src, className }) {
  return (
    <img
      src={src}
      alt=""
      className={`${className} shrink-0 select-none pointer-events-none`}
      draggable={false}
      style={{ objectFit: "contain" }}
    />
  );
}

/** App Store — real Apple icon PNG. */
function AppStoreIcon({ className }) {
  return <PngIcon src="/icons/appstore.png" className={className} />;
}

/** Find My — real Apple icon PNG. */
function FindMyIcon({ className }) {
  return <PngIcon src="/icons/findmy.png" className={className} />;
}

const REGISTRY = {
  mail: MailIcon,
  messages: MessagesIcon,
  imessage: MessagesIcon,
  whatsapp: WhatsAppIcon,
  phone: PhoneIcon,
  facetime: FaceTimeIcon,
  calendar: CalendarIcon,
  settings: SettingsIcon,
  photos: PhotosIcon,
  "app store": AppStoreIcon,
  appstore: AppStoreIcon,
  "find my": FindMyIcon,
  findmy: FindMyIcon,
};

export function IOSAppIcon({ name, className = "w-7 h-7" }) {
  const key = (name || "").trim().toLowerCase();
  const Cmp = REGISTRY[key];
  if (Cmp) return <Cmp className={className} />;
  return <FallbackTile name={name} className={className} />;
}

export default IOSAppIcon;
